import React, { memo, type FC } from 'react';
import { ActivityIndicator, type ActivityIndicatorProps } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui/text';
import { STRINGS } from '@/constants/strings';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import type { TypographyToken } from '@/theme/typography';

export type LoadingProps = {
  /**
   * Loading text displayed next to / below the indicator.
   * If omitted, only the indicator is rendered.
   */
  text?: string;
  /**
   * Layout direction for indicator + text.
   */
  direction?: 'row' | 'column';
  /**
   * Spacing between indicator and text.
   */
  gap?: number;
  /**
   * Text style token.
   */
  textVariant?: TypographyToken;
  /**
   * ActivityIndicator props passthrough.
   */
  indicatorSize?: ActivityIndicatorProps['size'];
  indicatorColor?: ActivityIndicatorProps['color'];
};

export const Loading: FC<LoadingProps> = memo(
  ({
    direction = 'column',
    gap,
    textVariant = 'Font Inter/Subtitle/Regular 12px',
    indicatorSize = 'small',
    indicatorColor = colors.Blue,
  }) => {
    const { scale } = useScaling();
    const resolvedGap = gap ?? scale(8);

    return (
      <Stack direction={direction} align="center" justify="center" gap={resolvedGap}>
        <ActivityIndicator size={indicatorSize} color={indicatorColor} />
        <Text variant={textVariant}>{STRINGS.loading}</Text>
      </Stack>
    );
  },
);
